import { Calendar, CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { DateTime } from "luxon";
import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { HorizontalDayList } from "@/domains/appointment/calendar/components/horizontal-day-list";
import { MonthDateNavigation } from "@/domains/appointment/calendar/components/month-date-navigation";
import { WeekDateNavigation } from "@/domains/appointment/calendar/components/week-date-navigation";
import { useIsMobile } from "@/hooks/hooks/use-mobile";
import type { NewCalendarViewMode } from "../types";

interface ResponsiveDatePickerProps {
  viewMode: NewCalendarViewMode;
  currentDate: DateTime;
  onDateChange: (date: DateTime) => void;
}

export function ResponsiveDatePicker({
  viewMode,
  currentDate,
  onDateChange,
}: ResponsiveDatePickerProps) {
  const isMobile = useIsMobile();

  // Calculate navigation unit and display format based on view mode
  const navigationConfig = useMemo(() => {
    switch (viewMode) {
      case "week":
        return {
          unit: "week" as const,
          format: "MMM dd",
          displayFormat: (date: DateTime) => {
            const start = date.startOf("week");
            const end = date.endOf("week");
            return `${start.toFormat("MMM dd")} - ${end.toFormat("MMM dd, yyyy")}`;
          },
          icon: CalendarDays,
        };
      case "month":
        return {
          unit: "month" as const,
          format: "MMMM yyyy",
          displayFormat: (date: DateTime) => date.toFormat("MMMM yyyy"),
          icon: Calendar,
        };
      default:
        return {
          unit: "day" as const,
          format: isMobile ? "MMM dd" : "EEEE, MMMM dd, yyyy",
          displayFormat: (date: DateTime) =>
            isMobile ? date.toFormat("MMM dd, yyyy") : date.toFormat("EEEE, MMMM dd, yyyy"),
          icon: Calendar,
        };
    }
  }, [viewMode, isMobile]);

  const handlePrevious = () => {
    const newDate = currentDate.minus({ [navigationConfig.unit]: 1 });
    onDateChange(newDate);
  };

  const handleNext = () => {
    const newDate = currentDate.plus({ [navigationConfig.unit]: 1 });
    onDateChange(newDate);
  };

  const handleToday = () => {
    onDateChange(DateTime.now());
  };

  const isToday = currentDate.hasSame(DateTime.now(), navigationConfig.unit);
  const DisplayIcon = navigationConfig.icon;

  // For list, daily, worker, and 3-day modes, use horizontal date picker like in old calendar
  if (
    viewMode === "list" ||
    viewMode === "daily" ||
    viewMode === "worker" ||
    viewMode === "3-day"
  ) {
    return (
      <div className="px-0">
        <HorizontalDayList
          currentDate={currentDate}
          onDateSelect={onDateChange}
          isMobile={isMobile}
          className="w-full"
        />
      </div>
    );
  }

  // For week mode, use the existing WeekDateNavigation component
  if (viewMode === "week") {
    return (
      <div className="px-4 py-3">
        <WeekDateNavigation
          currentDate={currentDate}
          onDateChange={onDateChange}
          onPrevious={handlePrevious}
          onNext={handleNext}
        />
      </div>
    );
  }

  // For month mode, use the existing MonthDateNavigation component
  if (viewMode === "month") {
    return (
      <div className="px-4 py-3">
        <MonthDateNavigation
          currentDate={currentDate}
          onDateChange={onDateChange}
          onPrevious={handlePrevious}
          onNext={handleNext}
        />
      </div>
    );
  }

  if (isMobile) {
    // Mobile: Compact horizontal date picker for other modes
    return (
      <div className="flex items-center justify-between px-4 py-2">
        <Button variant="ghost" size="sm" onClick={handlePrevious} className="h-8 w-8 p-0">
          <ChevronLeft className="h-4 w-4" />
        </Button>

        <div className="flex items-center gap-2">
          <DisplayIcon className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">{navigationConfig.displayFormat(currentDate)}</span>
        </div>

        <Button variant="ghost" size="sm" onClick={handleNext} className="h-8 w-8 p-0">
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  // Desktop: Full date picker with today button for non-list modes
  return (
    <div className="flex items-center justify-between px-6 py-3">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={handlePrevious} className="h-9">
          <ChevronLeft className="h-4 w-4 mr-1" />
          Previous
        </Button>

        <Button variant="ghost" size="sm" onClick={handleNext} className="h-9">
          Next
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 text-foreground">
          <DisplayIcon className="h-4 w-4" />
          <span className="font-medium">{navigationConfig.displayFormat(currentDate)}</span>
        </div>

        {!isToday && (
          <Button variant="outline" size="sm" onClick={handleToday} className="h-9">
            Today
          </Button>
        )}
      </div>
    </div>
  );
}
